import React from "react";
import { Formik, Field } from "formik";
import queryString from "query-string";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { FiLock, FiEye, FiEyeOff } from "react-icons/fi";

import api from "../../services/api";
import LoadingButton from "../../components/loadingButton";
import IconSpace1 from "../../assets/spaces-icons/1.png";

export default function Reset() {
  const navigate = useNavigate();
  const location = useLocation();
  const [seePassword, setSeePassword] = React.useState(false);
  const [seePassword1, setSeePassword1] = React.useState(false);

  const handleUpdatePassword = async (values) => {
    try {
      const { token } = queryString.parse(location.search);
      const { ok, code } = await api.post("/user/forgot_password_reset", { ...values, token });
      if (!ok) {
        if (code === "PASSWORD_TOKEN_EXPIRED_OR_INVALID") {
          return toast.error("Password reset link has expired or is invalid");
        }
        if (code === "PASSWORD_NOT_VALIDATED") {
          return toast.error("Password format is invalid");
        }
        return toast.error("Error updating password");
      }

      toast.success("Password successfully updated");
      navigate("/");
    } catch (e) {
      console.log(e);
      if (e.code === "PASSWORD_NOT_VALIDATED") {
        return toast.error("Password format is invalid");
      }

      toast.error("An error occurred while updating your password");
    }
  };

  const validateForm = (values) => {
    const errors = {};
    if (!values.password) {
      errors.password = "Password is required";
    }
    if (!values.password1) {
      errors.password1 = "Please confirm your password";
    }
    if (values.password && values.password1 && values.password !== values.password1) {
      errors.password1 = "Passwords do not match";
    }
    return errors;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-gray-900 flex items-center justify-center">
      <div className="relative max-w-xl w-full">
        <div className="relative bg-gray-900/80 backdrop-blur-sm rounded-xl border border-blue-500/20 shadow-2xl p-10">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center">
              <img src={IconSpace1} alt="Selego" className="w-16 h-16" />
            </div>
          </div>

          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold text-white">Create New Password</h1>
            <p className="text-gray-400 text-base mt-2">Enter your new password to complete the reset</p>
          </div>

          <div className="bg-blue-500/10 p-3 rounded-lg border border-blue-500/20 text-xs text-gray-300 mb-6">
            <strong>Password requirements:</strong> Minimum 6 characters, at least one letter
          </div>

          <Formik
            initialValues={{ password: "", password1: "" }}
            validate={validateForm}
            onSubmit={async (values, actions) => {
              handleUpdatePassword(values);
              actions.setSubmitting(false);
            }}>
            {({ values, errors, touched, isSubmitting, handleChange, handleSubmit }) => {
              return (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="text-base text-gray-300 flex items-center mb-2">
                      <FiLock className="mr-2 text-blue-400" size={20} />
                      New Password <span className="text-blue-400 ml-1">*</span>
                    </label>
                    <div className="relative">
                      <Field
                        className={`w-full px-4 py-3 rounded-lg bg-gray-800 border ${
                          errors.password && touched.password ? "border-red-500" : "border-gray-700"
                        } focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-white text-base`}
                        name="password"
                        type={seePassword ? "text" : "password"}
                        id="password"
                        value={values.password}
                        onChange={handleChange}
                        placeholder="Enter your new password"
                      />
                      <button type="button" onClick={() => setSeePassword(!seePassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-400">
                        {seePassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                      </button>
                    </div>
                    {errors.password && touched.password && <div className="text-red-400 text-sm mt-2">{errors.password}</div>}
                  </div>

                  <div>
                    <label className="text-base text-gray-300 flex items-center mb-2">
                      <FiLock className="mr-2 text-blue-400" size={20} />
                      Confirm Password <span className="text-blue-400 ml-1">*</span>
                    </label>
                    <div className="relative">
                      <Field
                        className={`w-full px-4 py-3 rounded-lg bg-gray-800 border ${
                          errors.password1 && touched.password1 ? "border-red-500" : "border-gray-700"
                        } focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-white text-base`}
                        name="password1"
                        type={seePassword1 ? "text" : "password"}
                        id="password1"
                        value={values.password1}
                        onChange={handleChange}
                        placeholder="Confirm your new password"
                      />
                      <button type="button" onClick={() => setSeePassword1(!seePassword1)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-400">
                        {seePassword1 ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                      </button>
                    </div>
                    {errors.password1 && touched.password1 && <div className="text-red-400 text-sm mt-2">{errors.password1}</div>}
                  </div>

                  <button
                    className="w-full py-3 rounded-lg text-base font-semibold text-white transition-all bg-blue-600 hover:bg-blue-700 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    type="submit"
                    disabled={isSubmitting || Object.keys(errors).length > 0 || !values.password || !values.password1}>
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Resetting password...
                      </div>
                    ) : (
                      "Reset Password"
                    )}
                  </button>
                </form>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
}
